import {
	Action,
	DEFAULT_LIMIT,
	DEFAULT_PAGE,
	Role,
	type UserAccessProfile,
} from "@pilatestime/shared-types";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Mail, Plus, Search, User, Users, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { fetchAccessProfileQueryOptions } from "@/api/accessProfile";
import { useInviteStaffMember } from "@/api/staff";
import {
	ListPageCard,
	ListPageCardContent,
	ListPageCardHeader,
} from "@/components/ListPageCard";
import { PageHeader } from "@/components/PageHeader";
import { RolePermissionsModal } from "@/components/RolePermissionsModal";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/ui/pagination";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { can, useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import {
	getOrganizationRoleIcon,
	getOrganizationRoleLabel,
} from "@/utils/roleUtils";
import { UnifiedStaffList } from "./-components/UnifiedStaffList";

// Search params schema
const staffSearchSchema = z.object({
	page: z.number().min(1).catch(DEFAULT_PAGE),
	limit: z.number().min(1).max(100).catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
});

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/staff/")({
	validateSearch: staffSearchSchema,
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Preload access profile
		const profile = await context.queryClient.ensureQueryData(
			fetchAccessProfileQueryOptions(userId, token, orgId),
		);

		// Guard: must have VIEW_STAFF permission
		const canView = can(profile as UserAccessProfile, Action.VIEW_STAFF);
		if (!canView) {
			throw new Error("Access denied to staff page");
		}

		return { userId, token, orgId };
	},
	component: StaffPage,
});

function StaffPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/staff/",
	});
	const search = Route.useSearch();
	const { userId, token } = Route.useLoaderData();

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1); // Reset to first page when search term changes
		}, 300); // 300ms delay

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Access control
	const can = useCan();
	const canManageStaff = can(Action.MANAGE_STAFF);
	const canInviteStaff = can(Action.INVITE_STAFF);

	// Mutations
	const inviteStaffMutation = useInviteStaffMember();

	// Pagination handlers
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handlePageSizeChange = useCallback((limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	}, []);

	const handleSearchChange = useCallback((newSearchTerm: string) => {
		setSearchTerm(newSearchTerm);
		// Page reset is handled by useEffect when debouncedSearchTerm changes
	}, []);

	const handlePaginationMetaChange = useCallback(
		(meta: typeof paginationMeta) => {
			setPaginationMeta(meta);
		},
		[],
	);

	// Invite staff state
	const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

	// Form state
	const [inviteFormData, setInviteFormData] = useState({
		email: "",
		role: Role.ORG_VIEWER,
		inviteeName: "",
	});

	// Validation function for invite form
	const isInviteFormValid = () => {
		return (
			inviteFormData.email.trim() !== "" &&
			inviteFormData.role !== undefined &&
			inviteFormData.role !== null
		);
	};

	const displayedCount = useMemo(() => {
		if (!paginationMeta.total) {
			return 0;
		}

		const startIndex = (paginationMeta.page - 1) * paginationMeta.limit;
		const remaining = paginationMeta.total - startIndex;

		if (remaining <= 0) {
			return 0;
		}

		return Math.min(paginationMeta.limit, remaining);
	}, [paginationMeta.limit, paginationMeta.page, paginationMeta.total]);

	const handleInviteStaff = async () => {
		if (!inviteFormData.email.trim()) {
			// Show validation error - this is not handled by mutation
			return;
		}

		try {
			await inviteStaffMutation.mutateAsync({
				token,
				organizationId: orgId,
				data: {
					email: inviteFormData.email.trim(),
					role: inviteFormData.role,
					inviteeName: inviteFormData.inviteeName.trim() || undefined,
				},
			});

			// Success toast is handled by the mutation hook
			setIsInviteDialogOpen(false);
			setInviteFormData({
				email: "",
				role: Role.ORG_VIEWER,
				inviteeName: "",
			});
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Failed to invite staff member:", error);
		}
	};

	return (
		<div className="h-full flex flex-col space-y-2 sm:space-y-3">
			{/* Header */}
			<PageHeader
				title={t("staff.management.title")}
				description={t("staff.management.description")}
				actions={
					canInviteStaff ? (
						<Button
							onClick={() => setIsInviteDialogOpen(true)}
							className="btn btn-primary btn-sm text-primary-content"
							data-testid="staff-invite-button"
							aria-label={t("staff.management.inviteNew")}
						>
							<Plus className="w-4 h-4" />
							<span className="hidden sm:inline">
								{t("staff.management.inviteNew")}
							</span>
						</Button>
					) : undefined
				}
			/>

			{/* Main content */}
			<ListPageCard>
				<ListPageCardHeader className="space-y-3">
					{/* Search */}
					<div className="relative flex-1 sm:max-w-md">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
						<Input
							type="search"
							placeholder={t("staff.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="pl-10 pr-10"
							data-testid="staff-search-input"
							autoComplete="off"
							data-1p-ignore="true"
							data-lpignore="true"
							data-form-type="other"
						/>
						<div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
							{searchTerm && (
								<button
									type="button"
									onClick={() => handleSearchChange("")}
									className="text-base-content/40 hover:text-base-content/80 transition-colors"
									aria-label={t("common.clear")}
								>
									<X className="h-3 w-3" />
								</button>
							)}
							{searchTerm !== debouncedSearchTerm && (
								<div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary" />
							)}
						</div>
					</div>
					{/* Results count */}
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-base-content/60">
						<span className="whitespace-nowrap">
							{t("common.showingResults", {
								count: displayedCount,
								total: paginationMeta.total,
							})}
						</span>
						{debouncedSearchTerm && (
							<>
								<span className="text-base-content/30">·</span>
								<span
									className="badge badge-ghost badge-sm max-w-50 truncate"
									title={debouncedSearchTerm}
								>
									{t("common.search")}:
									<span className="ml-1 font-medium">
										{debouncedSearchTerm}
									</span>
								</span>
							</>
						)}
					</div>
				</ListPageCardHeader>
				<ListPageCardContent>
					{/* Unified Staff and Invitations Table */}
					<div className="flex-1 overflow-hidden">
						<UnifiedStaffList
							organizationId={orgId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							searchTerm={debouncedSearchTerm}
							userId={userId}
							token={token}
							canManageStaff={canManageStaff}
							onPaginationMetaChange={handlePaginationMetaChange}
						/>
					</div>
					{/* Pagination */}
					<div className="shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
				</ListPageCardContent>
			</ListPageCard>

			{/* Invite staff dialog */}
			{canInviteStaff && (
				<Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
					<DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto mx-2 sm:mx-auto">
						<DialogHeader className="space-y-3 pb-6 border-b border-base-300">
							<DialogTitle className="text-xl font-semibold text-base-content flex items-center gap-2">
								<Users className="h-5 w-5 text-primary" />
								{t("staff.management.inviteNew")}
							</DialogTitle>
							<DialogDescription className="text-base-content/70">
								{t("staff.management.inviteDescription")}
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-6 py-6">
							{/* Contact Information Section */}
							<div className="space-y-4">
								<div className="flex items-center gap-2 pb-2 border-b border-base-200">
									<div className="w-2 h-2 bg-primary rounded-full" />
									<h3 className="text-lg font-medium text-base-content">
										{t("staff.form.sections.contact")}
									</h3>
								</div>
								<div className="grid gap-4">
									<div className="space-y-2">
										<Label
											htmlFor="email"
											className="text-sm font-medium text-base-content"
										>
											{t("staff.form.email")}
											<span className="text-error ml-1">*</span>
										</Label>
										<div className="relative">
											<Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
											<Input
												id="email"
												type="email"
												value={inviteFormData.email}
												onChange={(e) =>
													setInviteFormData((prev) => ({
														...prev,
														email: e.target.value,
													}))
												}
												placeholder={t("staff.form.emailPlaceholder")}
												className="pl-10 h-10"
											/>
										</div>
									</div>
									<div className="space-y-2">
										<Label
											htmlFor="inviteeName"
											className="text-sm font-medium text-base-content"
										>
											{t("staff.form.name")}
										</Label>
										<div className="relative">
											<User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
											<Input
												id="inviteeName"
												value={inviteFormData.inviteeName}
												onChange={(e) =>
													setInviteFormData((prev) => ({
														...prev,
														inviteeName: e.target.value,
													}))
												}
												placeholder={t("staff.form.namePlaceholder")}
												className="pl-10 h-10"
											/>
										</div>
									</div>
								</div>
							</div>

							{/* Role & Permissions Section */}
							<div className="space-y-4">
								<div className="flex items-center gap-2 pb-2 border-b border-base-200">
									<div className="w-2 h-2 bg-primary rounded-full" />
									<h3 className="text-lg font-medium text-base-content">
										{t("staff.form.sections.permissions")}
									</h3>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="role"
										className="text-sm font-medium text-base-content"
									>
										{t("staff.form.organizationRole")}
										<span className="text-error ml-1">*</span>
									</Label>
									<p className="text-xs text-muted-foreground">
										{t("staff.form.organizationRoleDescription")}
									</p>
									<Select
										value={inviteFormData.role}
										onValueChange={(value: Role) =>
											setInviteFormData((prev) => ({
												...prev,
												role: value,
											}))
										}
									>
										<SelectTrigger className="h-10 w-full">
											<SelectValue
												placeholder={t("staff.form.selectOrganizationRole")}
											/>
										</SelectTrigger>
										<SelectContent className="max-w-[calc(100vw-2rem)]">
											<div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b">
												{t("staff.form.organizationRolesHeader")}
											</div>
											{Object.values(Role)
												.filter(
													(role) =>
														role === Role.ORG_ADMINISTRATOR ||
														role === Role.ORG_MANAGER ||
														role === Role.ORG_MEDICAL_PROFESSIONAL ||
														role === Role.ORG_ACCOUNTANT ||
														role === Role.ORG_VIEWER ||
														role === Role.ORG_INSTRUCTOR_CALENDAR_ONLY,
												)
												.map((role) => (
													<SelectItem
														key={role as string}
														value={role as string}
														className="*:[span]:last:min-w-0"
													>
														<div className="flex items-center gap-3 min-w-0">
															{getOrganizationRoleIcon(role)}
															<div className="flex flex-col min-w-0">
																<span className="font-medium truncate">
																	{getOrganizationRoleLabel(role, t)}
																</span>
																{/* Hidden when echoed inside the closed trigger */}
																<span className="text-xs text-muted-foreground truncate in-data-[slot=select-value]:hidden">
																	{t(
																		`staff.form.roleDescriptions.${(role as string).toLowerCase()}`,
																	)}
																</span>
															</div>
														</div>
													</SelectItem>
												))}
										</SelectContent>
									</Select>
									<div className="flex justify-start mt-3">
										<RolePermissionsModal />
									</div>
								</div>
							</div>
						</div>
						<DialogFooter className="flex gap-3 pt-6 border-t border-base-300">
							<Button
								variant="outline"
								onClick={() => setIsInviteDialogOpen(false)}
								className="flex-1 h-11"
							>
								{t("common.cancel")}
							</Button>
							<Button
								type="button"
								onClick={handleInviteStaff}
								disabled={inviteStaffMutation.isPending || !isInviteFormValid()}
								className="flex-1 h-11 bg-primary hover:bg-primary/90 text-primary-content"
							>
								{inviteStaffMutation.isPending
									? t("common.sending")
									: t("staff.form.sendInvite")}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}
